import { ACCEPTED_FILE_TYPES, ACCEPTED_IMAGE_TYPES, ACCEPTED_VIDEO_TYPES } from "@/constants/file-picker"

export type AppChatAttachment = {
  filename: string
  mime: string
  dataUrl: string
}

export type AppChatComposerAttachment = AppChatAttachment & {
  id: string
}

type AttachmentModel = {
  capabilities?: {
    attachment?: boolean
    input?: {
      text?: boolean
      image?: boolean
      pdf?: boolean
      video?: boolean
    }
  }
}

const TEXT_FILE_TYPES = ACCEPTED_FILE_TYPES.filter(
  (item) =>
    !ACCEPTED_IMAGE_TYPES.includes(item) &&
    !ACCEPTED_VIDEO_TYPES.includes(item) &&
    item !== "application/pdf",
)

export type ModelAttachmentSupportError = {
  reason: "attachment" | "image" | "pdf" | "text" | "video"
  filename?: string
  message: string
}

function attachmentReason(mime: string): ModelAttachmentSupportError["reason"] {
  if (mime.startsWith("image/")) return "image"
  if (mime.startsWith("video/")) return "video"
  if (mime === "application/pdf") return "pdf"
  return "text"
}

export function modelAcceptedFileTypes(model: AttachmentModel | undefined) {
  const capabilities = model?.capabilities
  if (!capabilities?.attachment) return []
  return [
    ...(capabilities.input?.image ? ACCEPTED_IMAGE_TYPES : []),
    ...(capabilities.input?.video ? ACCEPTED_VIDEO_TYPES : []),
    ...(capabilities.input?.pdf ? ["application/pdf"] : []),
    ...(capabilities.input?.text ? TEXT_FILE_TYPES : []),
  ]
}

/** What the composer's file picker should offer for the active model.
 *
 *  The picker used the full static ACCEPTED_FILE_TYPES list regardless of model,
 *  so it happily offered files the send gate would then refuse — the same
 *  offers-what-it-rejects mismatch that made attachment failures so confusing.
 *
 *  Falls back to the full list when the model advertises no attachment support:
 *  `modelAcceptedFileTypes` returns [] there, and an empty `accept` attribute
 *  means "accept anything" in HTML, which is the opposite of the intent. The
 *  send gate still blocks those, now with an accurate message. */
export function composerAcceptedFileTypes(model: AttachmentModel | undefined) {
  const accepted = modelAcceptedFileTypes(model)
  return accepted.length > 0 ? accepted : ACCEPTED_FILE_TYPES
}

export function modelMimeSupported(model: AttachmentModel | undefined, mime: string) {
  const capabilities = model?.capabilities
  if (!capabilities?.attachment) return false
  if (mime.startsWith("image/")) return Boolean(capabilities.input?.image)
  if (mime.startsWith("video/")) return Boolean(capabilities.input?.video)
  if (mime === "application/pdf") return Boolean(capabilities.input?.pdf)
  return mime === "text/plain" && Boolean(capabilities.input?.text)
}

export function modelAttachmentSupportError(
  model: AttachmentModel | undefined,
  attachments: AppChatAttachment[],
): ModelAttachmentSupportError | undefined {
  if (!attachments.length) return
  const unsupported = attachments.find((attachment) => !modelMimeSupported(model, attachment.mime))
  if (!unsupported) return
  const reason = attachmentReason(unsupported.mime)
  if (!model?.capabilities?.attachment) {
    // The model takes NO attachments — the file's own type is irrelevant. Reporting
    // the mime-derived reason here ("text", "image", …) sent people off to check the
    // wrong capability; a custom provider that omits `attachment` on a model resolves
    // to false while input.text still defaults to true, so the mime reason is actively
    // misleading in exactly the case that produces this error.
    return {
      reason: "attachment",
      filename: unsupported.filename,
      message: "Current model does not support attachments.",
    }
  }
  return {
    reason,
    filename: unsupported.filename,
    message: `Current model cannot use ${unsupported.filename}.`,
  }
}

export const appChatAcceptedFileTypes = modelAcceptedFileTypes

export const appChatMimeSupported = modelMimeSupported

export function appChatAttachmentUnsupported(model: AttachmentModel | undefined, attachments: AppChatAttachment[]) {
  return modelAttachmentSupportError(model, attachments)?.message
}
